import { io, Socket } from 'socket.io-client';
import { SocketBasicRequest } from '../../models/Socket/socket-request';
import { SocketUserTypeEnum } from '../../models/Socket/socket-enum';
import { getAppSettings } from '../../utils';

export type SocketEventHandler = (payload: any) => void;

export class CashierSocketService {
  private static instance: CashierSocketService;
  private socketIOUrl: string | null = null;
  private socket: Socket | null = null;
  private eventHandlers: Map<string, SocketEventHandler[]> = new Map();

  public static getInstance(request?: SocketBasicRequest): CashierSocketService {
    if (!CashierSocketService.instance) {
      CashierSocketService.instance = new CashierSocketService();
    }
    if (request) {
      CashierSocketService.instance.connect(request);
    }
    return CashierSocketService.instance;
  }

  constructor() {
    const settings = getAppSettings();
    this.socketIOUrl = settings?.notificationsUrl || null;
  }

  connect(request: SocketBasicRequest): Socket | null {
    if (!this.socketIOUrl || !request.userID) {
      console.warn('[SocketService] Cannot connect: missing URL or userID');
      return null;
    }

    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }

    console.log(`[SocketService] Connecting to ${this.socketIOUrl} for user ${request.userID}`);

    this.socket = io(this.socketIOUrl, {
      query: {
        userID: request.userID,
        userType: SocketUserTypeEnum.PLAYER,
        createdAt: new Date(),
      },
      auth: {
        token: request.accessToken,
      },
      transports: ['websocket', 'polling'],
    });

    this.socket.on('connect', () => {
      console.log(`[SocketService] Connected with socket ID: ${this.socket?.id}`);
      this.socket?.emit('login-player', {
        userID: request.userID,
        accessToken: request.accessToken,
      });
    });

    this.socket.on('disconnect', reason => {
      console.log(`[SocketService] Disconnected: ${reason}`);
    });

    this.socket.on('connect_error', error => {
      console.error('[SocketService] Connection error:', error.message);
    });

    this.socket.on('notification-received', (payload: any) => {
      console.log('[SocketService] Notification received:', payload);
      this.emit('notification-received', payload);

      if (payload?.action) {
        this.emit(payload.action, payload.data);
      }
    });

    return this.socket;
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      console.log('[SocketService] Disconnected');
    }
  }

  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  on(event: string, handler: SocketEventHandler): void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, []);
    }
    this.eventHandlers.get(event)!.push(handler);
  }

  off(event: string, handler?: SocketEventHandler): void {
    if (!handler) {
      this.eventHandlers.delete(event);
      return;
    }
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  private emit(event: string, data: any): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error(`[SocketService] Error in handler for ${event}:`, error);
        }
      });
    }
  }
}

export const getSocketService = () => CashierSocketService.getInstance();

export const initializeSocket = (userID: string, accessToken: string): CashierSocketService => {
  return CashierSocketService.getInstance({ userID, accessToken });
};
